import React, { useState } from 'react'
import {
  Flex,
  Box,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
  Button,
  ButtonGroup,
  VStack,
  useDisclosure,
  Input,
  InputGroup,
  InputLeftElement,
  Tag,
  HStack,
  useToast,
  Select,
} from '@chakra-ui/react'
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table'
import Card from 'components/card/Card'
import { SkeletonTable } from 'components/skeleton/Skeleton'
import { AiOutlineDelete, AiOutlinePlus, AiOutlineSearch, AiOutlineCopy, AiOutlineEdit } from 'react-icons/ai'
import { MdEventSeat, MdDesignServices, MdPlace } from 'react-icons/md'
import { useGetSeatingPlans, useDuplicateSeatingPlan } from 'queries/seatingPlan.query'
import { useGetVenues } from 'queries/venue.query'
import { CreatePlanModal } from './CreatePlanModal'
import { DeletePlanModal } from './DeletePlanModal'
import { EditPlanModal } from './EditPlanModal'
import { useNavigate, useSearchParams } from 'react-router-dom'

const columnHelper = createColumnHelper()

export default function SeatingPlanTable() {
  const [searchParams] = useSearchParams()
  const venueIdParam = searchParams.get('venue_id') || ''
  const [selectedVenueId, setSelectedVenueId] = useState(venueIdParam)
  const [search, setSearch] = useState('')
  const [selectedPlan, setSelectedPlan] = useState(null)
  const navigate = useNavigate()
  const toast = useToast()

  const textColor = useColorModeValue('secondaryGray.900', 'white')
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100')
  const TheadBackground = useColorModeValue('white', 'gray.800')
  const hoverBackgroundColor = useColorModeValue('gray.100', 'gray.700')

  const { isOpen: isCreateOpen, onOpen: onOpenCreate, onClose: onCloseCreate } = useDisclosure()
  const { isOpen: isEditOpen, onOpen: onOpenEdit, onClose: onCloseEdit } = useDisclosure()
  const { isOpen: isDeleteOpen, onOpen: onOpenDelete, onClose: onCloseDelete } = useDisclosure()

  const { data: venuesData } = useGetVenues(1, 100)
  const venues = venuesData?.data || (Array.isArray(venuesData) ? venuesData : [])

  const { data, isLoading } = useGetSeatingPlans(1, 50, selectedVenueId || null, search)
  const duplicateMutation = useDuplicateSeatingPlan()

  const plansList = React.useMemo(() => {
    if (data?.data) return data.data
    if (Array.isArray(data)) return data
    return []
  }, [data])

  const handleDuplicate = async (plan) => {
    try {
      await duplicateMutation.mutateAsync(plan.id)
      toast({
        title: 'Plan duplicated',
        description: `Copy of ${plan.name} created successfully`,
        status: 'success',
        duration: 3000,
      })
    } catch (err) {
      toast({
        title: 'Failed to duplicate plan',
        status: 'error',
        duration: 3000,
      })
    }
  }

  const columns = React.useMemo(
    () => [
      columnHelper.accessor('name', {
        header: 'Plan Name',
        cell: (info) => (
          <VStack align="start" spacing={1}>
            <Text color={textColor} fontWeight="700" fontSize="sm">
              {info.getValue()}
            </Text>
            {info.row.original.venue && (
              <Flex align="center" gap={1} color="gray.500" fontSize="xs">
                <MdPlace />
                <Text>{info.row.original.venue.name} ({info.row.original.venue.city})</Text>
              </Flex>
            )}
          </VStack>
        ),
      }),
      columnHelper.accessor('total_seats', {
        header: 'Seats',
        cell: (info) => (
          <Flex align="center" gap={1.5}>
            <MdEventSeat color="#7551FF" />
            <Text color={textColor} fontWeight="700" fontSize="sm">
              {Number(info.getValue() || 0).toLocaleString()} seats
            </Text>
          </Flex>
        ),
      }),
      columnHelper.display({
        id: 'categories',
        header: 'Categories',
        cell: (info) => {
          const cats = info.row.original.plan_json?.categories || []
          if (!cats.length) return <Text fontSize="xs" color="gray.400">-</Text>
          return (
            <HStack spacing={1} wrap="wrap">
              {cats.slice(0, 4).map((c, i) => (
                <Tag
                  key={i}
                  size="sm"
                  borderRadius="full"
                  variant="solid"
                  style={{ backgroundColor: c.color || '#4A5568' }}
                  fontSize="10px"
                  px={2}
                >
                  {c.name}
                </Tag>
              ))}
              {cats.length > 4 && (
                <Tag size="sm" colorScheme="gray" fontSize="10px">
                  +{cats.length - 4}
                </Tag>
              )}
            </HStack>
          )
        },
      }),
      columnHelper.display({
        id: 'actions',
        header: 'Actions',
        cell: (info) => (
          <HStack spacing={2}>
            <Button
              size="sm"
              colorScheme="brand"
              leftIcon={<MdDesignServices />}
              onClick={() => navigate(`/admin/seating-plans/${info.row.original.id}/edit`)}
            >
              Open Visual Editor
            </Button>
            <ButtonGroup size="sm" isAttached variant="outline">
              <Button
                title="Edit Plan"
                onClick={() => {
                  setSelectedPlan(info.row.original)
                  onOpenEdit()
                }}
              >
                <AiOutlineEdit />
              </Button>
              <Button
                title="Duplicate Plan"
                isLoading={duplicateMutation.isPending}
                onClick={() => handleDuplicate(info.row.original)}
              >
                <AiOutlineCopy />
              </Button>
              <Button
                colorScheme="red"
                title="Delete Plan"
                onClick={() => {
                  setSelectedPlan(info.row.original)
                  onOpenDelete()
                }}
              >
                <AiOutlineDelete />
              </Button>
            </ButtonGroup>
          </HStack>
        ),
      }),
    ],
    [textColor, navigate, duplicateMutation.isPending, handleDuplicate, onOpenDelete, onOpenEdit]
  )

  const table = useReactTable({
    data: plansList,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  })

  if (isLoading) return <SkeletonTable />

  return (
    <Card flexDirection="column" w="100%" px="0px" overflowX={{ sm: 'scroll', lg: 'hidden' }}>
      <Flex px="25px" mb="20px" justifyContent="space-between" align="center" flexWrap="wrap" gap={3}>
        <VStack align="start" spacing={0}>
          <Text color={textColor} fontSize="22px" fontWeight="700" lineHeight="100%">
            Venue Seating Plans
          </Text>
          <Text color="gray.400" fontSize="sm" mt={1}>
            Design and manage interactive seat layouts, stages, VIP zones, and tier allocations
          </Text>
        </VStack>

        <Flex gap={3} align="center" flexWrap="wrap">
          <Select
            size="sm"
            maxW="200px"
            borderRadius="12px"
            placeholder="All Venues"
            value={selectedVenueId}
            onChange={(e) => setSelectedVenueId(e.target.value)}
          >
            {venues.map((v) => (
              <option key={v.id} value={v.id}>
                {v.name}
              </option>
            ))}
          </Select>

          <InputGroup size="sm" maxW="220px">
            <InputLeftElement pointerEvents="none">
              <AiOutlineSearch color="gray.300" />
            </InputLeftElement>
            <Input
              placeholder="Search seating plans..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              borderRadius="12px"
            />
          </InputGroup>

          <Button
            leftIcon={<AiOutlinePlus />}
            colorScheme="brand"
            size="sm"
            borderRadius="12px"
            onClick={onOpenCreate}
          >
            New Plan
          </Button>
        </Flex>
      </Flex>

      <Box overflowX="auto">
        <Table variant="simple" color="gray.500" mb="24px">
          <Thead bg={TheadBackground}>
            {table.getHeaderGroups().map((headerGroup) => (
              <Tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <Th
                    key={header.id}
                    borderColor={borderColor}
                    py="12px"
                  >
                    {flexRender(header.column.columnDef.header, header.getContext())}
                  </Th>
                ))}
              </Tr>
            ))}
          </Thead>
          <Tbody>
            {table.getRowModel().rows.length === 0 ? (
              <Tr>
                <Td colSpan={columns.length} textAlign="center" py={8}>
                  No seating plans found. Click "New Plan" to get started.
                </Td>
              </Tr>
            ) : (
              table.getRowModel().rows.map((row) => (
                <Tr key={row.id} _hover={{ bg: hoverBackgroundColor }}>
                  {row.getVisibleCells().map((cell) => (
                    <Td key={cell.id} borderColor={borderColor} py="14px">
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </Td>
                  ))}
                </Tr>
              ))
            )}
          </Tbody>
        </Table>
      </Box>

      <CreatePlanModal isOpen={isCreateOpen} onClose={onCloseCreate} />
      <EditPlanModal isOpen={isEditOpen} onClose={onCloseEdit} plan={selectedPlan} />
      <DeletePlanModal isOpen={isDeleteOpen} onClose={onCloseDelete} plan={selectedPlan} />
    </Card>
  )
}
